import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { AuthContext } from "shared/context/AuthContext";

const TTL = 1000 * 60 * 5;
const REQUEST_TIMEOUT = 30_000;

const readCache = (cacheKey) => {
  try {
    const stored = localStorage.getItem(cacheKey);

    if (!stored) return null;

    const parsed = JSON.parse(stored);

    return Array.isArray(parsed) ? parsed : null;
  } catch {
    return null;
  }
};

const readCacheTime = (cacheTimeKey) => {
  try {
    return Number(localStorage.getItem(cacheTimeKey)) || 0;
  } catch {
    return 0;
  }
};

export const useOkdProjectServices = (projectName) => {
  const { state, setContext } = useContext(AuthContext);

  const cacheKey = `okdServices_${projectName}`;
  const cacheTimeKey = `okdServicesLastFetch_${projectName}`;

  const initialCachedData = useMemo(
    () => readCache(cacheKey),
    [cacheKey]
  );

  const initialCachedLastFetch = useMemo(
    () => readCacheTime(cacheTimeKey),
    [cacheTimeKey]
  );

  const cacheDataRef = useRef(initialCachedData);
  const cacheTimeRef = useRef(initialCachedLastFetch);
  const abortControllerRef = useRef(null);

  const isInitialCacheValid =
    Array.isArray(initialCachedData) &&
    initialCachedLastFetch > 0 &&
    Date.now() - initialCachedLastFetch < TTL;

  const [data, setData] = useState(initialCachedData || []);
  const [loading, setLoading] = useState(!isInitialCacheValid);
  const [error, setError] = useState(null);

  const hasValidCache = useCallback(() => {
    return (
      Array.isArray(cacheDataRef.current) &&
      cacheTimeRef.current > 0 &&
      Date.now() - cacheTimeRef.current < TTL
    );
  }, []);

  const fetchData = useCallback(
    async (force = false) => {
      if (
        !projectName ||
        !state.isAuthUser ||
        state.isOkdAvailable !== true
      ) {
        setLoading(false);
        return false;
      }

      if (!force && hasValidCache()) {
        setData(cacheDataRef.current);
        setLoading(false);
        return true;
      }

      abortControllerRef.current?.abort();

      const controller = new AbortController();
      abortControllerRef.current = controller;

      let didTimeout = false;

      const timeoutId = setTimeout(() => {
        didTimeout = true;
        controller.abort();
      }, REQUEST_TIMEOUT);

      setLoading(true);
      setError(null);

      try {
        const encodedProjectName =
          encodeURIComponent(projectName);

        const resp = await fetch(
          `${process.env.REACT_APP_API_URL}` +
            "/ti/api/okd/user-projects/" +
            `${encodedProjectName}/services`,
          {
            credentials: "include",
            signal: controller.signal,
          }
        );

        const result = await resp.json().catch(() => null);

        if (resp.status === 401) {
          setContext({
            isOkdAvailable: false,
          });

          throw new Error(
            result?.message ||
              result?.detail ||
              "Brak aktywnego tokenu OKD"
          );
        }

        if (!resp.ok) {
          throw new Error(
            result?.message ||
              result?.detail ||
              `HTTP ${resp.status}`
          );
        }

        if (
          result?.status !== "success" ||
          !Array.isArray(result.services)
        ) {
          throw new Error("Invalid services response");
        }

        const fetchedAt = Date.now();

        cacheDataRef.current = result.services;
        cacheTimeRef.current = fetchedAt;

        setData(result.services);

        localStorage.setItem(
          cacheKey,
          JSON.stringify(result.services)
        );

        localStorage.setItem(
          cacheTimeKey,
          fetchedAt.toString()
        );

        return true;
      } catch (err) {
        if (err.name === "AbortError") {
          if (didTimeout) {
            const timeoutError = new Error(
              `Przekroczono czas pobierania usług projektu ${projectName}`
            );

            console.error(
              "Failed to fetch OKD services:",
              timeoutError
            );

            setError(timeoutError);
          }

          return false;
        }

        console.error(
          `Failed to fetch services for ${projectName}:`,
          err
        );

        if (cacheDataRef.current) {
          setData(cacheDataRef.current);
        }

        setError(err);
        return false;
      } finally {
        clearTimeout(timeoutId);

        if (abortControllerRef.current === controller) {
          setLoading(false);
        }
      }
    },
    [
      projectName,
      state.isAuthUser,
      state.isOkdAvailable,
      cacheKey,
      cacheTimeKey,
      hasValidCache,
      setContext,
    ]
  );

  useEffect(() => {
    if (
      !projectName ||
      !state.isAuthUser ||
      state.isOkdAvailable !== true
    ) {
      setLoading(false);
      return;
    }

    fetchData();

    return () => {
      abortControllerRef.current?.abort();
    };
  }, [
    projectName,
    state.isAuthUser,
    state.isOkdAvailable,
    fetchData,
  ]);

  const refetch = useCallback(() => {
    if (
      !projectName ||
      !state.isAuthUser ||
      state.isOkdAvailable !== true
    ) {
      return Promise.resolve(false);
    }

    return fetchData(true);
  }, [
    projectName,
    state.isAuthUser,
    state.isOkdAvailable,
    fetchData,
  ]);

  return {
    data,
    loading,
    error,
    refetch,
  };
};